import * as z from 'zod';

export const ParcelSelectionSchema = z.object({
  block: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[0-9A-Z]{1,6}$/),
  lot: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[0-9A-Z]{1,6}$/),
});
export const AddressResultsSchema = z.object({
  addresses: z
    .array(
      ParcelSelectionSchema.extend({
        address: z.string().trim().min(1).max(300),
        parcelAddress: z.string().trim().min(1).max(300).optional(),
      }),
    )
    .max(12),
});

const DbiListingSchema = z.object({
  id: z.string().min(1).max(120),
  kind: z.enum(['building', 'electrical', 'plumbing', 'complaint', 'nov']),
  status: z.string().max(300),
  date: z.string().max(100),
  address: z.string().max(500),
  sourceUrl: z.url().max(2_000),
  sourceOrder: z.number().int().nonnegative().optional(),
  description: z.string().max(5_000).optional(),
});

const NormalizedPageSchema = z.object({
  title: z.string(),
  fields: z.record(z.string(), z.string()),
  tables: z.array(
    z.object({
      title: z.string(),
      headers: z.array(z.string()),
      rows: z.array(z.array(z.string())),
    }),
  ),
  links: z.array(z.object({ label: z.string(), url: z.url() })),
});

export const PublicPropertyDiscoverySchema = z.object({
  schemaVersion: z.literal(1),
  cache: z.enum(['fresh', 'refreshed', 'stale']),
  failedAttempt: z.object({ attemptedAt: z.iso.datetime(), error: z.string() }).optional(),
  discovery: z.object({
    parserVersion: z.literal(11),
    block: ParcelSelectionSchema.shape.block,
    lot: ParcelSelectionSchema.shape.lot,
    addresses: z.array(z.string()).max(30),
    observedAt: z.iso.datetime(),
    sourceUrl: z.url(),
    records: z.array(DbiListingSchema).max(300),
    fetchedPages: z.number().int().nonnegative(),
  }),
});

const ParcelRecordSchema = DbiListingSchema.extend({
  description: z.string(),
  pages: z.array(NormalizedPageSchema),
});

const EventBaseSchema = z.object({
  schemaVersion: z.literal(1),
  parserVersion: z.literal(11),
  block: ParcelSelectionSchema.shape.block,
  lot: ParcelSelectionSchema.shape.lot,
});

export const PublicPropertyEnrichmentEventSchema = z.discriminatedUnion('type', [
  EventBaseSchema.extend({
    type: z.literal('discovery'),
    discovery: PublicPropertyDiscoverySchema.shape.discovery,
    cache: z.enum(['fresh', 'refreshed', 'stale']),
    failedAttempt: z.object({ attemptedAt: z.iso.datetime(), error: z.string() }).optional(),
  }),
  EventBaseSchema.extend({
    type: z.literal('record'),
    record: ParcelRecordSchema,
    cache: z.enum(['fresh', 'refreshed', 'stale']),
    observedAt: z.iso.datetime(),
    failedAttempt: z.object({ attemptedAt: z.iso.datetime(), error: z.string() }).optional(),
  }),
  EventBaseSchema.extend({
    type: z.literal('record-failed'),
    listing: DbiListingSchema,
    attemptedAt: z.iso.datetime(),
    error: z.string(),
  }),
  EventBaseSchema.extend({
    type: z.literal('source'),
    source: z.enum([
      'dbi',
      'assessor',
      'recorder',
      'subdivision',
      'sf311',
      'public-works',
      'section604',
      'fire-inspections',
      'fire-violations',
      'fire-compliance',
    ]),
    value: z.record(z.string(), z.unknown()).nullable(),
    cache: z.enum(['fresh', 'refreshed', 'stale']),
    observedAt: z.iso.datetime(),
    failedAttempt: z.object({ attemptedAt: z.iso.datetime(), error: z.string() }).optional(),
  }),
  EventBaseSchema.extend({
    type: z.literal('safety-scope'),
    scope: z.object({
      type: z.enum(['parcel', 'condo-building']),
      parcelCount: z.number().int().nonnegative(),
      parcels: z.array(ParcelSelectionSchema.extend({ address: z.string().max(500) })),
    }),
  }),
  EventBaseSchema.extend({
    type: z.literal('source-failed'),
    source: z.enum([
      'dbi',
      'assessor',
      'recorder',
      'subdivision',
      'sf311',
      'public-works',
      'section604',
      'fire-inspections',
      'fire-violations',
      'fire-compliance',
    ]),
    attemptedAt: z.iso.datetime(),
    error: z.string(),
    value: z.record(z.string(), z.unknown()).nullable().optional(),
  }),
  EventBaseSchema.extend({ type: z.literal('complete') }),
  EventBaseSchema.extend({
    type: z.literal('analysis'),
    enrichments: z.array(z.unknown()).max(1000),
  }),
  EventBaseSchema.extend({
    type: z.literal('source-skipped'),
    source: z.literal('subdivision'),
    value: z.record(z.string(), z.unknown()).optional(),
  }),
  EventBaseSchema.extend({ type: z.literal('failed'), error: z.string() }),
]);
